import React from "react";

type CompanyPageProps = {
  header?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  card?: boolean;
  padded?: boolean;
};

type SlotProps = {
  children?: React.ReactNode;
  className?: string;
};

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

const HeaderLeft = ({ children, className }: SlotProps) => (
  <div className={cx("company-page-header-left", className)}>{children}</div>
);

const HeaderRight = ({ children, className }: SlotProps) => (
  <div className={cx("company-page-header-right", className)}>{children}</div>
);

const Heading = ({
  title,
  subtitle,
  extra,
  children,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  if (!title && !subtitle && !extra && !children) {
    return null;
  }

  return (
    <div className="company-page-heading">
      <div className="company-page-heading-text">
        {title ? <h3 className="company-page-title">{title}</h3> : null}
        {subtitle ? (
          <p className="company-page-subtitle">{subtitle}</p>
        ) : null}
        {children}
      </div>
      {extra ? <div className="company-page-heading-extra">{extra}</div> : null}
    </div>
  );
};

const Footer = ({ children, className }: SlotProps) => (
  <div className={cx("company-page-footer", className)}>{children}</div>
);

const FooterActions = ({ children, className }: SlotProps) => (
  <div className={cx("company-page-footer-actions", className)}>{children}</div>
);

const CompanyPage = ({
  header,
  title,
  subtitle,
  extra,
  footer,
  children,
  className,
  card = true,
  padded = true,
}: CompanyPageProps) => {
  const showHeader = Boolean(header);
  const heading = (
    <Heading title={title} subtitle={subtitle} extra={extra} />
  );

  const body = (
    <>
      {heading}
      {children}
      {footer ? <Footer>{footer}</Footer> : null}
    </>
  );

  return (
    <div className={cx("company-page", className)}>
      {showHeader ? (
        <div className="company-page-header">{header}</div>
      ) : null}
      <div
        className={cx(
          "company-page-body",
          showHeader && "has-header",
          !card && "is-plain"
        )}
      >
        {card ? (
          <div
            className={cx(
              "company-page-card",
              !padded && "is-flush"
            )}
          >
            {body}
          </div>
        ) : (
          body
        )}
      </div>
    </div>
  );
};

CompanyPage.HeaderLeft = HeaderLeft;
CompanyPage.HeaderRight = HeaderRight;
CompanyPage.Heading = Heading;
CompanyPage.Footer = Footer;
CompanyPage.FooterActions = FooterActions;

export default CompanyPage;
